import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { STORAGE_KEYS } from '../helpers/constants';

type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme mode from localStorage or default to 'system'
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
      return (saved as ThemeMode) || 'system';
    }
    return 'system';
  });
  
  // Determine if we should use dark mode based on theme mode and system preference
  const getSystemDarkMode = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  };

  const isDarkMode = themeMode === 'system' ? getSystemDarkMode() : themeMode === 'dark';

  // Listen for system theme changes when in 'system' mode
  React.useEffect(() => {
    if (themeMode === 'system' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        // Force re-render when system theme changes
        setThemeMode(themeMode);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#1cabde',
      },
      secondary: {
        main: '#f48fb1',
      },
      background: {
        default: isDarkMode ? '#121212' : '#f5f5f5',
        paper: isDarkMode ? '#1e1e1e' : '#ffffff',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: '#1cabde',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1899c7',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
              color: '#666666',
            },
          },
          outlinedPrimary: {
            borderColor: '#1cabde',
            color: '#1cabde',
            '&:hover': {
              backgroundColor: 'rgba(28, 171, 222, 0.08)',
            },
          },
        },
      },
    },
  });

  // Wrapper function to save theme mode to localStorage
  const handleSetThemeMode = (mode: ThemeMode) => {
    setThemeMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);
    }
  };

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode: handleSetThemeMode, isDarkMode }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 